package io.peekandpoke.kraft.addons.threejs

import io.peekandpoke.kraft.addons.registry.Addon
import io.peekandpoke.kraft.addons.registry.AddonKey
import io.peekandpoke.kraft.addons.registry.AddonRegistry
import io.peekandpoke.kraft.addons.registry.AddonRegistryBuilder
import io.peekandpoke.kraft.addons.threejs.js.AmbientLight
import io.peekandpoke.kraft.addons.threejs.js.BoxGeometry
import io.peekandpoke.kraft.addons.threejs.js.CanvasTexture
import io.peekandpoke.kraft.addons.threejs.js.CylinderGeometry
import io.peekandpoke.kraft.addons.threejs.js.DirectionalLight
import io.peekandpoke.kraft.addons.threejs.js.Group
import io.peekandpoke.kraft.addons.threejs.js.HemisphereLight
import io.peekandpoke.kraft.addons.threejs.js.MaterialParameters
import io.peekandpoke.kraft.addons.threejs.js.Mesh
import io.peekandpoke.kraft.addons.threejs.js.MeshBasicMaterial
import io.peekandpoke.kraft.addons.threejs.js.MeshLambertMaterial
import io.peekandpoke.kraft.addons.threejs.js.MeshStandardMaterial
import io.peekandpoke.kraft.addons.threejs.js.Object3D
import io.peekandpoke.kraft.addons.threejs.js.OrthographicCamera
import io.peekandpoke.kraft.addons.threejs.js.PerspectiveCamera
import io.peekandpoke.kraft.addons.threejs.js.PlaneGeometry
import io.peekandpoke.kraft.addons.threejs.js.PointLight
import io.peekandpoke.kraft.addons.threejs.js.Raycaster
import io.peekandpoke.kraft.addons.threejs.js.Scene
import io.peekandpoke.kraft.addons.threejs.js.SphereGeometry
import io.peekandpoke.kraft.addons.threejs.js.SpotLight
import io.peekandpoke.kraft.addons.threejs.js.TorusGeometry
import io.peekandpoke.kraft.addons.threejs.js.Vector2
import io.peekandpoke.kraft.addons.threejs.js.Vector3
import io.peekandpoke.kraft.addons.threejs.js.WebGLRenderer
import io.peekandpoke.kraft.addons.threejs.js.WebGLRendererParameters
import kotlinx.coroutines.await
import org.w3c.dom.HTMLCanvasElement
import kotlin.js.Promise
import kotlin.math.PI

/**
 * Facade for the lazily-loaded three.js addon.
 *
 * Provides type-safe access to three.js for 3D WebGL rendering.
 */
class ThreeJsAddon internal constructor(
    private val threeModule: dynamic,
) {
    /** Raw module handle — escape hatch for APIs not covered by the curated externals. */
    @Suppress("unused")
    val raw: dynamic get() = threeModule

    //  Scene / Camera / Renderer  /////////////////////////////////////////////////////////////////////

    fun createScene(): Scene {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.Scene
        return js("new ctor()").unsafeCast<Scene>()
    }

    @Suppress("unused")
    fun createPerspectiveCamera(
        fov: Double = 50.0,
        aspect: Double = 1.0,
        near: Double = 0.1,
        far: Double = 2000.0,
    ): PerspectiveCamera {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.PerspectiveCamera
        return js("new ctor(fov, aspect, near, far)").unsafeCast<PerspectiveCamera>()
    }

    @Suppress("unused")
    fun createOrthographicCamera(
        left: Double, right: Double, top: Double, bottom: Double,
        near: Double = 0.1, far: Double = 2000.0,
    ): OrthographicCamera {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.OrthographicCamera
        return js("new ctor(left, right, top, bottom, near, far)").unsafeCast<OrthographicCamera>()
    }

    @Suppress("unused")
    fun createWebGLRenderer(parameters: WebGLRendererParameters): WebGLRenderer {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.WebGLRenderer
        return js("new ctor(parameters)").unsafeCast<WebGLRenderer>()
    }

    //  Geometry  //////////////////////////////////////////////////////////////////////////////////////

    @Suppress("unused")
    fun createBoxGeometry(width: Double = 1.0, height: Double = 1.0, depth: Double = 1.0): BoxGeometry {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.BoxGeometry
        return js("new ctor(width, height, depth)").unsafeCast<BoxGeometry>()
    }

    @Suppress("unused")
    fun createSphereGeometry(radius: Double = 1.0, widthSegments: Int = 32, heightSegments: Int = 16): SphereGeometry {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.SphereGeometry
        return js("new ctor(radius, widthSegments, heightSegments)").unsafeCast<SphereGeometry>()
    }

    @Suppress("unused")
    fun createPlaneGeometry(width: Double = 1.0, height: Double = 1.0): PlaneGeometry {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.PlaneGeometry
        return js("new ctor(width, height)").unsafeCast<PlaneGeometry>()
    }

    @Suppress("unused")
    fun createCylinderGeometry(
        radiusTop: Double = 1.0, radiusBottom: Double = 1.0, height: Double = 1.0, radialSegments: Int = 32,
    ): CylinderGeometry {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.CylinderGeometry
        return js("new ctor(radiusTop, radiusBottom, height, radialSegments)").unsafeCast<CylinderGeometry>()
    }

    @Suppress("unused")
    fun createTorusGeometry(
        radius: Double = 1.0, tube: Double = 0.4, radialSegments: Int = 12, tubularSegments: Int = 48,
    ): TorusGeometry {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.TorusGeometry
        return js("new ctor(radius, tube, radialSegments, tubularSegments)").unsafeCast<TorusGeometry>()
    }

    //  Materials  /////////////////////////////////////////////////////////////////////////////////////

    @Suppress("unused")
    fun createMeshBasicMaterial(parameters: MaterialParameters): MeshBasicMaterial {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.MeshBasicMaterial
        return js("new ctor(parameters)").unsafeCast<MeshBasicMaterial>()
    }

    @Suppress("unused")
    fun createMeshStandardMaterial(parameters: MaterialParameters): MeshStandardMaterial {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.MeshStandardMaterial
        return js("new ctor(parameters)").unsafeCast<MeshStandardMaterial>()
    }

    @Suppress("unused")
    fun createMeshLambertMaterial(parameters: MaterialParameters): MeshLambertMaterial {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.MeshLambertMaterial
        return js("new ctor(parameters)").unsafeCast<MeshLambertMaterial>()
    }

    //  Mesh  //////////////////////////////////////////////////////////////////////////////////////////

    @Suppress("unused")
    fun createMesh(geometry: dynamic, material: dynamic): Mesh {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.Mesh
        return js("new ctor(geometry, material)").unsafeCast<Mesh>()
    }

    //  Lights  ////////////////////////////////////////////////////////////////////////////////////////

    @Suppress("unused")
    fun createAmbientLight(color: dynamic = 0xffffff, intensity: Double = 1.0): AmbientLight {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.AmbientLight
        return js("new ctor(color, intensity)").unsafeCast<AmbientLight>()
    }

    @Suppress("unused")
    fun createDirectionalLight(color: dynamic = 0xffffff, intensity: Double = 1.0): DirectionalLight {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.DirectionalLight
        return js("new ctor(color, intensity)").unsafeCast<DirectionalLight>()
    }

    @Suppress("unused")
    fun createPointLight(
        color: dynamic = 0xffffff, intensity: Double = 1.0, distance: Double = 0.0, decay: Double = 2.0,
    ): PointLight {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.PointLight
        return js("new ctor(color, intensity, distance, decay)").unsafeCast<PointLight>()
    }

    @Suppress("unused")
    fun createHemisphereLight(
        skyColor: dynamic = 0xffffff, groundColor: dynamic = 0x444444, intensity: Double = 1.0,
    ): HemisphereLight {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.HemisphereLight
        return js("new ctor(skyColor, groundColor, intensity)").unsafeCast<HemisphereLight>()
    }

    //  Math  //////////////////////////////////////////////////////////////////////////////////////////

    @Suppress("unused")
    fun createVector3(x: Double = 0.0, y: Double = 0.0, z: Double = 0.0): Vector3 {
        @Suppress("UnusedVariable", "unused")
        val ctor = threeModule.Vector3
        return js("new ctor(x, y, z)").unsafeCast<Vector3>()
    }

    @Suppress("unused")
    fun createVector2(x: Double = 0.0, y: Double = 0.0): Vector2 = create("Vector2", x, y)

    @Suppress("unused")
    fun createRaycaster(): Raycaster = create("Raycaster")

    //  Scene graph  ///////////////////////////////////////////////////////////////////////////////////

    @Suppress("unused")
    fun createObject3D(): Object3D = create("Object3D")

    @Suppress("unused")
    fun createGroup(): Group = create("Group")

    //  More lights  ///////////////////////////////////////////////////////////////////////////////////

    /** A spot light; [angle] in radians (at most PI / 2), [penumbra] from 0 (hard edge) to 1. */
    @Suppress("unused")
    fun createSpotLight(
        color: dynamic = 0xffffff,
        intensity: Double = 1.0,
        distance: Double = 0.0,
        angle: Double = PI / 3.0,
        penumbra: Double = 0.0,
        decay: Double = 2.0,
    ): SpotLight = create("SpotLight", color, intensity, distance, angle, penumbra, decay)

    //  Textures  //////////////////////////////////////////////////////////////////////////////////////

    @Suppress("unused")
    fun createCanvasTexture(canvas: HTMLCanvasElement): CanvasTexture = create("CanvasTexture", canvas)

    //  Any class  /////////////////////////////////////////////////////////////////////////////////////

    /**
     * A new instance of three.js's class [className], made with [args]: for a class the addon has no constructor for.
     * [T] is not checked; fails with the name when three.js has no such class.
     */
    fun <T> create(className: String, vararg args: Any?): T {
        // An own export that is a function: inherited names ("constructor", "toString") are not classes of the module
        val module: dynamic = threeModule
        val own = js("Object.prototype.hasOwnProperty").call(module, className).unsafeCast<Boolean>()
        val ctor: dynamic = threeModule[className]

        require(own && jsTypeOf(ctor) == "function") { "three.js has no class '$className'" }

        return js("Reflect").construct(ctor, args).unsafeCast<T>()
    }
}

/** Key for the three.js addon. */
val threeJsAddonKey = AddonKey<ThreeJsAddon>("threejs")

/** Registers the three.js addon for lazy loading via dynamic import. */
fun AddonRegistryBuilder.threeJs(lazy: Boolean = false): Addon<ThreeJsAddon> = register(
    key = threeJsAddonKey,
    name = "threejs",
    lazy = lazy,
) {
    @Suppress("UnsafeCastFromDynamic")
    val module: dynamic = (js("import('three')") as Promise<dynamic>).await()

    ThreeJsAddon(
        threeModule = module.default ?: module,
    )
}

/** Accessor for the three.js addon on the registry. */
val AddonRegistry.threeJs: Addon<ThreeJsAddon>
    get() = this[threeJsAddonKey]
